import React, { useState, useEffect } from 'react';
import { X, Edit2, Bookmark } from 'lucide-react';
import {
  getSavedSearches,
  deleteSavedSearch,
  renameSavedSearch,
  applySavedSearch
} from '../services/savedSearchService';
import useFilterStore from '../store/filterStore';

/**
 * SavedSearches Component
 * Displays saved searches as horizontal scrollable chips
 */
const SavedSearches = ({
  onApplySearch,
  className = ''
}) => {
  const { setAllFilters } = useFilterStore();
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [activeSearchId, setActiveSearchId] = useState(null);

  // Fetch saved searches on mount
  useEffect(() => {
    loadSavedSearches();
  }, []);

  const loadSavedSearches = async () => {
    try {
      setLoading(true);
      const response = await getSavedSearches();
      if (response.success) {
        setSavedSearches(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load saved searches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplySearch = async (search) => {
    try {
      // Update lastUsed timestamp
      await applySavedSearch(search._id);
      // Apply the filters via filter store
      if (search.filters) {
        setAllFilters(search.filters);
      }
      // Set as active
      setActiveSearchId(search._id);
      // Callback if provided
      if (onApplySearch) {
        onApplySearch(search);
      }
      // Reload to update order
      loadSavedSearches();
    } catch (err) {
      console.error('Failed to apply search:', err);
    }
  };

  const handleDelete = async (e, searchId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this saved search?')) {
      return;
    }

    try {
      await deleteSavedSearch(searchId);
      setSavedSearches(prev => prev.filter(s => s._id !== searchId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartEdit = (e, search) => {
    e.stopPropagation();
    setEditingId(search._id);
    setEditName(search.name);
  };

  const handleSaveEdit = async (e, searchId) => {
    e.stopPropagation();
    if (!editName.trim()) {
      setEditingId(null);
      return;
    }

    try {
      await renameSavedSearch(searchId, editName.trim());
      setSavedSearches(prev =>
        prev.map(s =>
          s._id === searchId ? { ...s, name: editName.trim() } : s
        )
      );
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditName('');
  };

  if (loading) {
    return null;
  }

  if (savedSearches.length === 0) {
    return null;
  }

  return (
    <div className={`${className}`}>
      {/* Label */}
      <div className="flex items-center gap-2 mb-2">
        <Bookmark className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-600">Saved Searches</span>
      </div>

      {/* Error message */}
      {error && (
        <div className="text-xs text-red-600 mb-2">{error}</div>
      )}

      {/* Scrollable chips container */}
      <div className="flex flex-wrap gap-2">
        {savedSearches.map((search) => (
          <div
            key={search._id}
            onClick={() => !editingId && handleApplySearch(search)}
            className={`
              group relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm
              border transition-all cursor-pointer
              ${activeSearchId === search._id
                ? 'bg-primary-100 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }
            `}
          >
            {editingId === search._id ? (
              <>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit(e, search._id);
                    if (e.key === 'Escape') handleCancelEdit(e);
                  }}
                  className="w-24 px-1 py-0 text-sm border-none focus:ring-0 bg-transparent"
                  autoFocus
                />
                <button
                  onClick={(e) => handleSaveEdit(e, search._id)}
                  className="text-primary-600 hover:text-primary-700 text-xs"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600 text-xs"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="max-w-[150px] truncate">{search.name}</span>

                {/* Action buttons - visible on hover */}
                <div className="hidden group-hover:flex items-center gap-1">
                  <button
                    onClick={(e) => handleStartEdit(e, search)}
                    className="p-0.5 text-gray-400 hover:text-gray-600"
                    title="Rename"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, search._id)}
                    className="p-0.5 text-gray-400 hover:text-red-500"
                    title="Delete"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedSearches;
